import { useEffect, useState, useCallback } from "react";
import { Inbox, MapPin, Building2, ChevronRight, RefreshCw, DollarSign, Clock, Send, Play, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  requested: "bg-amber-50 text-amber-800 border-amber-200",
  quoted: "bg-blue-50 text-blue-800 border-blue-200",
  accepted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  active: "bg-indigo-50 text-indigo-800 border-indigo-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  canceled: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminEngagements() {
  const [engagements, setEngagements] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // detail/quote dialog
  const [quoteForm, setQuoteForm] = useState({ quote_amount: 0, quote_terms: "", quote_validity_days: 14, payment_mode: "manual" });
  const [adminNotes, setAdminNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/engagements", { params: { status: statusFilter || undefined } });
      setEngagements(data.engagements || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load engagements");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (e) => {
    setActive(e);
    setQuoteForm({
      quote_amount: e.quote_amount || 0,
      quote_terms: e.quote_terms || "",
      quote_validity_days: 14,
      payment_mode: e.payment_mode || "manual",
    });
    setAdminNotes([]);
    setNoteDraft("");
    try {
      const { data } = await api.get(`/admin/engagements/${e.id}/notes`);
      setAdminNotes(data.notes || []);
    } catch (_err) { /* ignore */ }
  };

  const postEngagementNote = async () => {
    if (!noteDraft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/engagements/${active.id}/notes`, { body: noteDraft.trim() });
      setAdminNotes([...adminNotes, data.note]);
      setNoteDraft("");
      toast.success("Private note added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally { setBusy(false); }
  };

  const deleteEngagementNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/engagements/${active.id}/notes/${nid}`);
      setAdminNotes(adminNotes.filter((n) => n.id !== nid));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const sendQuote = async () => {
    if (!quoteForm.quote_terms.trim()) return toast.error("Add quote terms");
    setBusy(true);
    try {
      await api.post(`/admin/engagements/${active.id}/quote`, {
        quote_amount: Number(quoteForm.quote_amount) || 0,
        quote_terms: quoteForm.quote_terms.trim(),
        quote_validity_days: Number(quoteForm.quote_validity_days) || 14,
        payment_mode: quoteForm.payment_mode,
      });
      toast.success("Quote saved");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save quote");
    } finally { setBusy(false); }
  };

  const sendUpdate = async () => {
    const body = window.prompt("Write the customer-visible update (will be emailed and appear on their timeline):");
    if (!body || !body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/engagements/${active.id}/send-update`, { body: body.trim() });
      toast.success(data.email_sent ? `Update sent + emailed to ${data.to}` : "Update posted (no email recipient on file)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send update");
    } finally { setBusy(false); }
  };

  const resendQuoteEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/engagements/${active.id}/resend-quote`);
      toast.success(`Quote email re-sent to ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend");
    } finally { setBusy(false); }
  };

  const startEngagement = async () => {
    if (!active.customer_user_id) return toast.error("Link a customer account first (Admin · Customers)");
    setBusy(true);
    try {
      await api.post(`/admin/engagements/${active.id}/start`);
      toast.success("Engagement started — project created on the customer's workspace");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start engagement");
    } finally { setBusy(false); }
  };

  const closeEngagement = async () => {
    if (!window.confirm("Close this engagement?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/engagements/${active.id}/close`, { note: "Closed by admin" });
      toast.success("Engagement closed");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close");
    } finally { setBusy(false); }
  };

  const counts = {};
  for (const e of engagements) counts[e.status] = (counts[e.status] || 0) + 1;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12" data-testid="admin-engagements-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">Admin · Expediter queue</div>
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900">Engagements</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Every lead that came in through <code className="font-mono text-xs">/hire</code>. Send a quote,
              start the engagement, and a project automatically spins up on the customer&apos;s workspace.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="engagements-reload">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />Reload
          </Button>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="engagement-status-chips">
          {[["", "All", engagements.length], ["requested", "Requested", counts.requested || 0], ["quoted", "Quoted", counts.quoted || 0],
            ["accepted", "Accepted", counts.accepted || 0], ["active", "Active", counts.active || 0], ["closed", "Closed", counts.closed || 0]].map(([v, l, c]) => (
            <button key={v} onClick={() => setStatusFilter(v)} data-testid={`engagement-filter-${v || "all"}`}
              className={`text-xs px-3 py-1.5 rounded-full font-mono border transition ${statusFilter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
              {l} <span className="ml-1 opacity-70">{c}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
        ) : engagements.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-12 text-center" data-testid="engagements-empty">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h2 className="font-outfit text-lg font-bold text-slate-900">No engagements match this filter.</h2>
            <p className="text-sm text-slate-500 mt-1">Share the /hire page link to start collecting leads.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3">Project / Jurisdiction</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {engagements.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`engagement-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 truncate">{e.contact_name || e.contact_email}</div>
                      <div className="text-[10px] text-slate-500 font-mono truncate">{e.contact_email}</div>
                      {e.company && <div className="text-[10px] text-slate-400 truncate">{e.company}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 truncate">{e.project_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono truncate flex items-center gap-1 mt-0.5">
                        <MapPin className="w-2.5 h-2.5" />{e.jurisdiction || "—"}
                      </div>
                      {e.service_needed && <div className="text-[10px] text-emerald-700 font-mono truncate mt-0.5">{e.service_needed}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`text-[10px] font-mono ${STATUS_COLORS[e.status] || ""}`}>{e.status}</Badge>
                      {e.quote_amount > 0 && (
                        <div className="text-[10px] text-slate-500 font-mono mt-1">${Number(e.quote_amount).toLocaleString()}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(e)}
                        data-testid={`engagement-open-${e.id}`} className="text-xs gap-1">
                        Open <ChevronRight className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail dialog */}
        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent className="max-w-2xl">
            {active && (
              <>
                <DialogHeader>
                  <DialogTitle>{active.project_name}</DialogTitle>
                  <DialogDescription>
                    From {active.contact_name || active.contact_email} · {active.jurisdiction || "no jurisdiction"}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-[10px] font-mono ${STATUS_COLORS[active.status] || ""}`}>{active.status}</Badge>
                    {active.customer_user_id ? (
                      <Badge variant="outline" className="text-[10px] font-mono bg-emerald-50 border-emerald-200 text-emerald-700">linked to customer</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] font-mono bg-amber-50 border-amber-200 text-amber-700">guest — not linked</Badge>
                    )}
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">Scope</div>
                    <div className="text-sm text-slate-900 whitespace-pre-wrap">{active.scope_description}</div>
                  </div>
                  {active.service_needed && (
                    <div><span className="text-[10px] font-mono uppercase text-slate-500">Service: </span>{active.service_needed}</div>
                  )}
                  {active.valuation > 0 && (
                    <div><span className="text-[10px] font-mono uppercase text-slate-500">Valuation: </span>${Number(active.valuation).toLocaleString()}</div>
                  )}

                  <div className="border-t border-slate-200 pt-4 bg-indigo-50/30 -mx-4 px-4 py-3 rounded-md" data-testid="engagement-admin-notes">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">Private notes · admin only</div>
                    {adminNotes.length > 0 && (
                      <ul className="space-y-2 mb-3 max-h-40 overflow-auto">
                        {adminNotes.map((n) => (
                          <li key={n.id} className="bg-white border border-indigo-100 rounded-md p-2.5 text-sm relative" data-testid={`engagement-note-${n.id}`}>
                            <div className="whitespace-pre-wrap text-slate-700">{n.body}</div>
                            <div className="text-[10px] text-slate-400 font-mono mt-1">{n.by_admin_email} · {new Date(n.at).toLocaleString()}</div>
                            <button onClick={() => deleteEngagementNote(n.id)} className="absolute top-1.5 right-1.5 text-slate-300 hover:text-rose-600 text-xs">×</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Textarea rows={2} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Sales context, vendor relationships, call notes, follow-up flags…"
                      data-testid="engagement-note-draft" />
                    <div className="mt-2 flex justify-end">
                      <Button size="sm" onClick={postEngagementNote} disabled={!noteDraft.trim() || busy}
                        data-testid="engagement-note-submit"
                        className="bg-indigo-600 hover:bg-indigo-500 text-white">
                        Add private note
                      </Button>
                    </div>
                  </div>

                  {active.quote_amount > 0 && active.payment_mode === "stripe" && active.stripe_checkout_url && (
                    <div className="border-t border-slate-200 pt-4 bg-indigo-50/40 -mx-4 -mb-2 px-4 py-3 rounded-md">
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">Stripe Checkout — live</div>
                      <a href={active.stripe_checkout_url} target="_blank" rel="noopener noreferrer" data-testid="stripe-checkout-link"
                        className="block text-xs font-mono text-indigo-700 hover:text-indigo-900 truncate underline">
                        {active.stripe_checkout_url}
                      </a>
                      <div className="text-[11px] text-slate-600 mt-1.5">
                        Customer sees a &ldquo;Pay ${Number(active.quote_amount).toLocaleString()} now&rdquo; button in their workspace.
                      </div>
                    </div>
                  )}

                  {active.status !== "closed" && active.status !== "canceled" && (
                    <div className="border-t border-slate-200 pt-4">
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">Quote</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Quote amount ($)</Label>
                          <Input type="number" min="0" value={quoteForm.quote_amount}
                            onChange={(e) => setQuoteForm({ ...quoteForm, quote_amount: e.target.value })}
                            data-testid="quote-amount" />
                        </div>
                        <div>
                          <Label className="text-xs">Valid for (days)</Label>
                          <Input type="number" min="1" max="90" value={quoteForm.quote_validity_days}
                            onChange={(e) => setQuoteForm({ ...quoteForm, quote_validity_days: e.target.value })}
                            data-testid="quote-validity" />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Terms / scope of work</Label>
                        <Textarea rows={4} value={quoteForm.quote_terms}
                          onChange={(e) => setQuoteForm({ ...quoteForm, quote_terms: e.target.value })}
                          placeholder="Fee, milestones, what's included…" data-testid="quote-terms" />
                      </div>
                      <div className="mt-3 flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-md">
                        <Label className="text-xs flex-1">Payment mode</Label>
                        <div className="flex gap-1 bg-white border border-slate-200 rounded-md p-0.5" data-testid="payment-mode-toggle">
                          <button type="button" onClick={() => setQuoteForm({ ...quoteForm, payment_mode: "manual" })}
                            data-testid="payment-mode-manual"
                            className={`text-xs px-3 py-1 rounded ${quoteForm.payment_mode === "manual" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                            Manual (invoice via email)
                          </button>
                          <button type="button" onClick={() => setQuoteForm({ ...quoteForm, payment_mode: "stripe" })}
                            data-testid="payment-mode-stripe"
                            className={`text-xs px-3 py-1 rounded ${quoteForm.payment_mode === "stripe" ? "bg-indigo-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                            Stripe Checkout
                          </button>
                        </div>
                      </div>
                      {quoteForm.payment_mode === "stripe" && (
                        <div className="mt-2 text-[11px] text-indigo-700 bg-indigo-50/60 border border-indigo-200 rounded-md p-2.5">
                          <strong>Coming soon:</strong> Stripe Checkout for the quoted amount will be generated and sent to the customer.
                          For now the mode is recorded on the engagement and you can still bill them manually.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter className="gap-2 flex-wrap">
                  <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
                  {(active.status === "requested" || active.status === "quoted") && (
                    <Button onClick={sendQuote} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="send-quote-btn">
                      <Send className="w-3.5 h-3.5 mr-2" />{active.status === "quoted" ? "Update quote" : "Send quote"}
                    </Button>
                  )}
                  {active.status === "quoted" && (
                    <Button variant="outline" onClick={resendQuoteEmail} disabled={busy} data-testid="resend-quote-btn"
                      className="text-blue-700 border-blue-300 hover:bg-blue-50">
                      <Send className="w-3.5 h-3.5 mr-2" />Resend email
                    </Button>
                  )}
                  {active.status !== "closed" && active.status !== "canceled" && active.status !== "requested" && (
                    <Button onClick={sendUpdate} disabled={busy} data-testid="send-update-btn"
                      className="bg-emerald-600 hover:bg-emerald-500 text-white">
                      <Send className="w-3.5 h-3.5 mr-2" />Send update
                    </Button>
                  )}
                  {(active.status === "accepted" || active.status === "quoted") && active.customer_user_id && (
                    <Button onClick={startEngagement} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="start-engagement-btn">
                      <Play className="w-3.5 h-3.5 mr-2" />Start engagement
                    </Button>
                  )}
                  {active.status !== "closed" && active.status !== "canceled" && (
                    <Button variant="outline" onClick={closeEngagement} disabled={busy} data-testid="close-engagement-btn"
                      className="text-rose-600 border-rose-300 hover:bg-rose-50">
                      Close
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
